import { PublicKey, VersionedTransaction } from '@solana/web3.js';
import { BN, SwapQuote, UnifiedSwapClient } from '@velocity-exchange/sdk';
import type { Connection } from '@solana/web3.js';

import { isValidQuote } from 'src/utils/swap';
import {
	MAX_TX_BYTE_SIZE,
	getSerializedTransactionSize,
} from 'src/utils/transaction';

export interface SwapTxAttempt {
	tx: VersionedTransaction;
	/** The quote the tx was built from — the one whose minOut must be deposited. */
	quote: SwapQuote;
	slippageBps: number;
}

/**
 * Builds the standalone wallet-side swap at the tightest slippage that still
 * simulates, walking up the ladder only as far as it has to.
 *
 * Slippage is baked into a quote — the provider takes no override when turning
 * one back into a transaction — so each rung is a fresh quote, not a re-signed
 * copy of the last one. Simulating before the wallet prompt is what makes the
 * tight first rung safe to try: an over-tight tolerance surfaces here, where
 * retrying is free, rather than as a failed send after the user has signed.
 *
 * Returns `null` with the last error when every rung fails. Callers must build
 * the deposit leg from the returned quote, never the one they came in with.
 */
export const buildSwapTxAtTightestSlippage = async ({
	connection,
	quoteClient,
	approvedQuote,
	inputMint,
	outputMint,
	amount,
	taker,
	slippageLadderBps,
	onRungFailed,
}: {
	connection: Connection;
	/** Must be the client that produced `approvedQuote`. */
	quoteClient: UnifiedSwapClient;
	/** The quote the user is looking at. Reused when it matches the first rung. */
	approvedQuote: SwapQuote;
	inputMint: PublicKey;
	outputMint: PublicKey;
	amount: BN;
	/** Wallet the route is quoted and built for. */
	taker: PublicKey;
	/** Tolerances to try, tightest first. */
	slippageLadderBps: readonly number[];
	onRungFailed?: (slippageBps: number, error: unknown) => void;
}): Promise<{ attempt: SwapTxAttempt | null; error?: unknown }> => {
	let lastError: unknown;

	for (const slippageBps of slippageLadderBps) {
		try {
			// The displayed quote is already at the first rung, and re-quoting it
			// would only hand the user a different route than the one they saw.
			const canReuseApproved =
				approvedQuote.slippageBps === slippageBps &&
				approvedQuote.inAmount === amount.toString();

			const quote = canReuseApproved
				? approvedQuote
				: await quoteClient.getQuote({
						inputMint,
						outputMint,
						amount,
						userPublicKey: taker,
						swapMode: 'ExactIn',
						slippageBps,
						autoSlippage: false,
					});

			const quoteError = quote?.error ?? 'missing required fields';
			if (!isValidQuote(quote)) {
				throw new Error(
					`Invalid swap quote at ${slippageBps}bps: ${quoteError}`
				);
			}

			const tx = await quoteClient.getSwapTransaction({
				quote,
				userPublicKey: taker,
			});

			// Size-check before simulating. An oversized tx fails inside web3.js
			// serialization with an opaque RangeError, and only after we've paid for
			// the round-trip.
			const txSize = getSerializedTransactionSize(tx);
			if (txSize > MAX_TX_BYTE_SIZE) {
				throw new Error(
					`Swap transaction too large: ${txSize} bytes (max ${MAX_TX_BYTE_SIZE})`
				);
			}

			// Unsigned, so signature verification is off and the provider's
			// blockhash gets replaced with a current one rather than failing the
			// simulation on age alone.
			const simulation = await connection.simulateTransaction(tx, {
				sigVerify: false,
				replaceRecentBlockhash: true,
			});

			if (simulation.value.err) {
				const error: Error & { logs?: string[] } = new Error(
					`Swap simulation failed at ${slippageBps}bps: ${JSON.stringify(
						simulation.value.err
					)}`
				);
				if (simulation.value.logs?.length) {
					error.logs = simulation.value.logs;
				}
				throw error;
			}

			return { attempt: { tx, quote, slippageBps } };
		} catch (err) {
			lastError = err;
			onRungFailed?.(slippageBps, err);
		}
	}

	return { attempt: null, error: lastError };
};
